import React from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useStudioPageChromeOverride } from "../../../common/StudioPageChromeContext";
import "./karkhana-ui.scss";
import { isKarkhanaHome, screenFor, trailFor } from "./karkhanaTrail";
import { useKT } from "../i18n/kt";
import { kp } from "../karkhanaPaths";

const Chevron = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" aria-hidden="true">
    <path d="M12.5 4.5 7 10l5.5 5.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" aria-hidden="true">
    <path d="M10 4.5v11M4.5 10h11" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export const GearIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="none">
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

/** True when this page was reached by navigating inside the app (not a reload or an opened link). */
export const cameFromInApp = (locationKey?: string, historyLength: number = window.history.length) =>
  Boolean(locationKey) && historyLength > 1;

/**
 * Small square "back" button, left of the page title (same size as the product page's icon buttons).
 * It goes back to wherever the person came from — Home → Cash book → Back returns Home, not Pay.
 * `to` is only the fallback when there is no in-app page to return to.
 */
export const BackLink = ({ to, label = "Back" }: { to: string; label?: string }) => {
  const history = useHistory();
  const location = useLocation();
  return (
    <Link
      to={to}
      className="kh-iconbtn"
      aria-label={label}
      title={label}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return; // new tab etc.: follow the link
        if (cameFromInApp(location.key)) {
          e.preventDefault();
          history.goBack();
        }
      }}
    >
      <Chevron />
    </Link>
  );
};

/** Compact header button. primary = the page's main action (dark, like "+ Add product"). */
export const HeaderLink = ({
  to,
  children,
  primary,
  add,
  title,
}: {
  to: string;
  children: React.ReactNode;
  primary?: boolean;
  add?: boolean;
  title?: string;
}) => (
  <Link to={to} className={`kh-btn${primary ? " is-primary" : ""}`} title={title}>
    {add ? <PlusIcon /> : null}
    <span>{children}</span>
  </Link>
);

export const HeaderButton = ({
  onClick,
  children,
  primary,
  add,
  disabled,
  title,
}: {
  onClick: () => void;
  children: React.ReactNode;
  primary?: boolean;
  add?: boolean;
  disabled?: boolean;
  title?: string;
}) => (
  <button type="button" className={`kh-btn${primary ? " is-primary" : ""}`} onClick={onClick} disabled={disabled} title={title}>
    {add ? <PlusIcon /> : null}
    <span>{children}</span>
  </button>
);

/**
 * Every Karkhana page starts the same way: the title, then small actions on the right. Back sits in
 * the top bar, left of the page name, exactly where every other studio page has it. It returns to
 * the page the person came from; `back` is only used when the page was opened directly.
 */
const PageHeader = ({
  title,
  back,
  crumbs,
  trail,
  children,
}: {
  title: React.ReactNode;
  back?: string;
  backLabel?: string;
  /** Pages between Karkhana and this one in the top bar trail, e.g. [{ to: kp.costings(), label: "Costing" }]. */
  crumbs?: { to: string; label: string }[];
  /** This page's name in the trail (with crumbs). */
  trail?: string;
  children?: React.ReactNode;
}) => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useKT();
  // Where this page sits, so someone 3–4 levels deep can jump to any level above (not only Back).
  const routeTrail = crumbs && crumbs.length ? crumbs : trailFor(location.pathname);
  // A page inside a tab (a person's day, a loom) names that tab in the trail: Karkhana › Attendance.
  const backScreen = back ? screenFor(back.split("?")[0]) : undefined;
  const path = routeTrail.length || !back || !backScreen ? routeTrail : [{ to: back, label: backScreen }];
  const here = trail || (typeof title === "string" ? title : undefined) || screenFor(location.pathname);
  // Every page but Home has a way back: its own parent, or Home for the tab pages (Daily work, Pay…).
  const home = isKarkhanaHome(location.pathname);
  const parent = back || (path.length ? path[path.length - 1].to : kp.home());
  const goBack = () => {
    if ((back || path.length) && cameFromInApp(location.key)) history.goBack();
    else history.push(parent);
  };
  useStudioPageChromeOverride({
    ...(home ? {} : { onBack: goBack }),
    ...(home
      ? {}
      : {
          crumbs: path.map((c) => ({ ...c, label: t(c.label) })),
          title: here ? t(here) : undefined,
        }),
  });
  return (
    <>
      {/* Phones hide the app bar on Karkhana pages, so the trail shows here on every page below a tab. */}
      {home ? null : (
        <nav className="kh-crumbs" aria-label={t("You are here")}>
          <Link to={kp.home()}>{t("Karkhana")}</Link>
          {path.map((c) => (
            <React.Fragment key={c.to}>
              <span aria-hidden="true">›</span>
              <Link to={c.to}>{t(c.label)}</Link>
            </React.Fragment>
          ))}
        </nav>
      )}
    <div className="kh-header">
      {/* Phones: the app's own bar is hidden on Karkhana pages, so this row carries Back (Figma top bar). */}
      {home ? null : (
        <button type="button" className="kh-header__back" onClick={goBack} aria-label={t("Back")}>
          ‹
        </button>
      )}
      <h2 className="kh-header__title">{title}</h2>
      {children ? <div className="kh-header__actions">{children}</div> : null}
    </div>
    </>
  );
};

export default PageHeader;
